import { useState, useEffect, useCallback, useRef } from 'react';
import { Text, Button, Badge, Tab, TabList, Spinner } from '@fluentui/react-components';
import { SlideTextRegular, ArrowLeftRegular, ArrowDownloadRegular, ArrowSyncRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import type { PresentationDocument } from '../services/presentation-service';
import { renderPresentationToObjectUrl } from '../services/presentation-service';
import { useApiToken } from '../hooks/useApiToken';

interface PresentationViewerPageProps {
  presentation: PresentationDocument;
  onBack: () => void;
}

/**
 * Viewer for Marp slide decks. Unlike DocumentViewerPage, the rendered tab
 * shows the Marp-rendered PDF inline (via blob URL in an iframe), not the
 * raw markdown source — Marp markdown displays poorly without its renderer.
 * The Markdown tab still shows the source for engineers reviewing the
 * template's Marp directives.
 */
export function PresentationViewerPage({ presentation, onBack }: PresentationViewerPageProps) {
  const intl = useIntl();
  const getToken = useApiToken();
  const [activeTab, setActiveTab] = useState('rendered');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  // Guard against React 18 StrictMode firing the auto-render effect twice in
  // dev — the Marp subprocess is a 7-second hit and racing two of them ends
  // with the first response's state-set being suppressed by the unmount of
  // the first effect instance. Skip the second auto-render; the user can
  // still re-render manually.
  const autoRenderFiredRef = useRef(false);

  const renderDeck = useCallback(async () => {
    setRendering(true);
    setRenderError(null);
    try {
      const token = await getToken();
      const url = await renderPresentationToObjectUrl(presentation.id!, presentation.customerId, token);
      // Revoke any prior URL — the blob handle is single-use per render.
      setPdfUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (err) {
      setRenderError(err instanceof Error ? err.message : 'Render failed');
    } finally {
      setRendering(false);
    }
  }, [presentation.id, presentation.customerId, getToken]);

  // Auto-render on mount so the user sees the deck immediately. The ref guard
  // makes this idempotent across StrictMode's intentional double-mount.
  useEffect(() => {
    if (autoRenderFiredRef.current) return;
    autoRenderFiredRef.current = true;
    void renderDeck();
    // No cleanup that revokes pdfUrl on unmount — the parent (App.tsx) only
    // mounts this page when the user opens a viewer, and revoking on every
    // re-render race would defeat the ref guard above. The blob is garbage-
    // collected when the page navigates away anyway.
  }, [renderDeck]);

  const downloadPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `${presentation.title.replace(/\s+/g, '-')}.pdf`;
    a.click();
  };

  return (
    <div className="page-root page-root--scrollable document-viewer-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'presentationViewer.backButton.title' })} />
        <SlideTextRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{presentation.title}</Text>
        <div className="title-bar-actions">
          <Button
            appearance="subtle"
            icon={<ArrowSyncRegular />}
            onClick={renderDeck}
            disabled={rendering}
            size="small"
            title={intl.formatMessage({ id: 'presentationViewer.rerenderButton.title' })}
          >
            {rendering ? intl.formatMessage({ id: 'presentationViewer.rerenderButton.rendering' }) : intl.formatMessage({ id: 'presentationViewer.rerenderButton.label' })}
          </Button>
          <Badge size="small" appearance="outline">{presentation.cafPhase}</Badge>
          {presentation.sessionNumber && (
            <Badge size="small" appearance="outline">{intl.formatMessage({ id: 'presentationViewer.badge.session' }, { number: presentation.sessionNumber })}</Badge>
          )}
          {presentation.slideCount && (
            <Badge size="small" appearance="outline">{intl.formatMessage({ id: 'presentationViewer.badge.slides' }, { count: presentation.slideCount })}</Badge>
          )}
          <Badge size="small" appearance="outline">v{presentation.version}</Badge>
          <Button
            appearance="subtle"
            icon={<ArrowDownloadRegular />}
            onClick={downloadPdf}
            disabled={!pdfUrl}
            title={intl.formatMessage({ id: 'presentationViewer.downloadButton.title' })}
          />
        </div>
      </div>

      <div className="viewer-tabs">
        <TabList selectedValue={activeTab} onTabSelect={(_e, d) => setActiveTab(d.value as string)}>
          <Tab value="rendered">{intl.formatMessage({ id: 'presentationViewer.tab.rendered' })}</Tab>
          <Tab value="markdown">{intl.formatMessage({ id: 'presentationViewer.tab.marpSource' })}</Tab>
        </TabList>
      </div>

      <div className="viewer-content">
        {activeTab === 'rendered' && (
          <div className="presentation-pdf-frame">
            {rendering && !pdfUrl && (
              <div className="presentation-pdf-loading">
                <Spinner size="medium" label={intl.formatMessage({ id: 'presentationViewer.spinner.rendering' })} />
              </div>
            )}
            {renderError && (
              <div className="page-error">
                <ErrorCircleRegular />
                <Text className="text-danger">{intl.formatMessage({ id: 'presentationViewer.renderError' }, { error: renderError })}</Text>
              </div>
            )}
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                title={presentation.title}
                className="presentation-pdf-iframe"
              />
            )}
          </div>
        )}
        {activeTab === 'markdown' && (
          <pre className="markdown-source">{presentation.markdownContent}</pre>
        )}
      </div>
    </div>
  );
}
